import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { useAutoSave, AutoSaveIndicator } from '../hooks/useAutoSave';

export const AddDelayModal = ({ projects, onClose, onSuccess, defaultProjectId, editDelay }) => {
  const isEdit = !!editDelay;
  const [form, setForm] = useState(
    isEdit
      ? {
          attach_type: editDelay.project_id ? 'project' : (editDelay.lead_id ? 'lead' : 'none'),
          project_id: editDelay.project_id || '',
          lead_id: editDelay.lead_id || '',
          delay_type: editDelay.delay_type || '',
          description: editDelay.description || '',
          request_date: editDelay.request_date || new Date().toISOString().split('T')[0],
          target_date: editDelay.target_date || '',
          comment: editDelay.comment || '',
          contact_name: editDelay.contact_name || '',
          contact_email: editDelay.contact_email || '',
          contact_phone: editDelay.contact_phone || '',
          assigned_users: editDelay.assigned_users || [],
          auto_relance: !!editDelay.auto_relance,
          include_in_works_schedule: !!editDelay.include_in_works_schedule,
          works_company: editDelay.works_company || '',
          works_phase: editDelay.works_phase || '',
          works_budget_ht: editDelay.works_budget_ht ?? '',
        }
      : { attach_type: defaultProjectId ? 'project' : 'project', project_id: defaultProjectId || '', lead_id: '', delay_type: '', description: '', request_date: new Date().toISOString().split('T')[0], target_date: '', comment: '', contact_name: '', contact_email: '', contact_phone: '', assigned_users: [], auto_relance: false, include_in_works_schedule: false, works_company: '', works_phase: '', works_budget_ht: '' }
  );
  const [team, setTeam] = useState([]);
  const [leads, setLeads] = useState([]);
  // Iter 100 : annuaire emails pour autocomplete du contact_email
  const [emailDirectory, setEmailDirectory] = useState([]);
  useEffect(() => {
    axios.get(`${API}/users/assignable`).then(r => setTeam(r.data || [])).catch(() => {});
    axios.get(`${API}/crm/leads`).then(r => setLeads((r.data || []).filter(l => !l.archived))).catch(() => {});
  }, []);
  useEffect(() => {
    const pid = form.project_id || defaultProjectId || '';
    const url = pid ? `${API}/delays/email-directory?project_id=${pid}` : `${API}/delays/email-directory`;
    axios.get(url).then(r => setEmailDirectory(r.data?.items || [])).catch(() => {});
  }, [form.project_id, defaultProjectId]);

  // Auto-save : declenche des qu'on a type + description + target_date (+ rattachement valide)
  const { status, savedAt, error, recordId, setRecordId } = useAutoSave({
    state: form,
    canCreate: (s) => {
      if (!s.delay_type || !s.description?.trim() || !s.target_date) return false;
      if (s.attach_type === 'project') return !!s.project_id;
      if (s.attach_type === 'lead') return !!s.lead_id;
      return true; // 'none' = delai libre / tache interne
    },
    createFn: async (s) => {
      const payload = { ...s };
      delete payload.attach_type;
      if (s.attach_type === 'project') { payload.lead_id = null; }
      else if (s.attach_type === 'lead') { payload.project_id = null; }
      else { payload.project_id = null; payload.lead_id = null; }
      // Sanitize works fields (envoie null si non rempli)
      if (payload.works_budget_ht === '' || payload.works_budget_ht == null) payload.works_budget_ht = null;
      else payload.works_budget_ht = parseFloat(payload.works_budget_ht) || null;
      if (!payload.include_in_works_schedule) {
        payload.works_company = null;
        payload.works_phase = null;
        payload.works_budget_ht = null;
      }
      const r = await axios.post(`${API}/delays`, payload);
      return { id: r.data.delay_id, doc: r.data };
    },
    updateFn: async (id, s) => {
      const { project_id, lead_id, attach_type, ...updates } = s;
      // Sanitize budget
      if (updates.works_budget_ht === '' || updates.works_budget_ht == null) updates.works_budget_ht = null;
      else updates.works_budget_ht = parseFloat(updates.works_budget_ht) || null;
      if (!updates.include_in_works_schedule) {
        updates.works_company = null;
        updates.works_phase = null;
        updates.works_budget_ht = null;
      }
      await axios.put(`${API}/delays/${id}`, updates);
    },
    debounceMs: 600,
  });

  React.useEffect(() => {
    if (isEdit && editDelay?.delay_id) setRecordId(editDelay.delay_id);
  }, [isEdit, editDelay, setRecordId]);

  const up = (k, v) => setForm({ ...form, [k]: v });
  const toggleAssign = (uid) => {
    const cur = form.assigned_users || [];
    setForm({ ...form, assigned_users: cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid] });
  };

  const handleClose = () => {
    if (recordId && onSuccess) onSuccess();
    else onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="add-delay-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl my-8 animate-fade-in">
        <div className="flex items-center justify-between mb-2"><h2 className="text-xl font-bold">{isEdit ? 'Modifier le delai' : 'Nouveau delai / tache'}</h2><button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button></div>
        <p className="text-xs text-gray-500 mb-4">{isEdit ? 'Modifications enregistrees automatiquement.' : 'Sauvegarde automatique des que type, description et date cible sont saisis.'}</p>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          {/* Selecteur du rattachement */}
          {!isEdit && !defaultProjectId && (
            <div>
              <label className="realease-label">Rattacher à</label>
              <div className="grid grid-cols-3 gap-2" data-testid="delay-attach-type">
                {[
                  { v: 'project', l: 'Projet', icon: '🏠' },
                  { v: 'lead', l: 'Client seul', icon: '👤' },
                  { v: 'none', l: 'Tâche perso', icon: '📝' },
                ].map(o => (
                  <button key={o.v} type="button" onClick={() => setForm({ ...form, attach_type: o.v, project_id: o.v === 'project' ? form.project_id : '', lead_id: o.v === 'lead' ? form.lead_id : '' })}
                          className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${form.attach_type === o.v ? 'border-[#008A8D] bg-[#008A8D]/5 text-[#006D72]' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                          data-testid={`attach-${o.v}`}>
                    <span className="mr-1">{o.icon}</span>{o.l}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {form.attach_type === 'project' && (
              <div className="md:col-span-2"><label className="realease-label">Projet *</label>
                <select value={form.project_id} onChange={e => up('project_id', e.target.value)} className="realease-input" data-testid="delay-project" disabled={!!defaultProjectId || isEdit}>
                  <option value="">Selectionner</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
            )}
            {form.attach_type === 'lead' && (
              <div className="md:col-span-2"><label className="realease-label">Client (lead sans projet) *</label>
                <select value={form.lead_id} onChange={e => up('lead_id', e.target.value)} className="realease-input" data-testid="delay-lead" disabled={isEdit}>
                  <option value="">Selectionner un client</option>
                  {leads.map(l => <option key={l.lead_id} value={l.lead_id}>{l.first_name} {l.last_name} {l.email ? `(${l.email})` : ''}</option>)}
                </select>
              </div>
            )}
            {form.attach_type === 'none' && (
              <div className="md:col-span-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800"><b>Tâche perso / collaborateur</b> — ce délai n'est rattaché à aucun client ni projet. Pratique pour un rappel ou une mission interne.</p>
              </div>
            )}
            <div><label className="realease-label">Type *</label>
              <select value={form.delay_type} onChange={e => up('delay_type', e.target.value)} className="realease-input" data-testid="delay-type">
                <option value="">Selectionner</option><option value="banque">Banque</option><option value="urbanisme">Urbanisme</option><option value="notaire">Notaire</option><option value="assurance">Assurance</option><option value="travaux">Travaux</option><option value="administratif">Administratif</option><option value="rdv">Rendez-vous</option><option value="rappel">Rappel / tâche</option><option value="autre">Autre</option>
              </select>
            </div>
            <div><label className="realease-label">Date cible *</label><input type="date" value={form.target_date} onChange={e => up('target_date', e.target.value)} className="realease-input" data-testid="delay-target-date" /></div>
          </div>
          <div><label className="realease-label">Description *</label><input type="text" value={form.description} onChange={e => up('description', e.target.value)} className="realease-input" data-testid="delay-description" /></div>
          <div><label className="realease-label">Date de demande</label><input type="date" value={form.request_date} onChange={e => up('request_date', e.target.value)} className="realease-input" /></div>

          {/* Interlocuteur externe (relance auto par email) - visible seulement avec rattachement projet/client */}
          {form.attach_type !== 'none' && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
              <p className="text-xs font-bold text-amber-900 uppercase tracking-wide">Interlocuteur (pour relance auto par email)</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <input type="text" value={form.contact_name} onChange={e => up('contact_name', e.target.value)} placeholder="Nom (M. Dupont)" className="realease-input text-sm" data-testid="contact-name" />
                <input
                  type="email"
                  value={form.contact_email}
                  onChange={e => up('contact_email', e.target.value)}
                  placeholder="email@exemple.fr"
                  className="realease-input text-sm"
                  list="delay-email-directory"
                  data-testid="contact-email"
                />
                <datalist id="delay-email-directory">
                  {emailDirectory.map((it) => (
                    <option key={it.email} value={it.email}>{it.name ? `${it.name} (${it.source})` : it.source}</option>
                  ))}
                </datalist>
                <input type="tel" value={form.contact_phone} onChange={e => up('contact_phone', e.target.value)} placeholder="Telephone" className="realease-input text-sm" data-testid="contact-phone" />
              </div>
              <p className="text-[11px] text-amber-700">Si un email est rempli, le bouton "Relancer" enverra un message structure automatique a cet interlocuteur.</p>

              <label className={`flex items-start gap-3 mt-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${form.auto_relance ? 'border-[#008A8D] bg-[#008A8D]/5' : 'border-gray-200 bg-white hover:border-gray-300'} ${!form.contact_email ? 'opacity-60' : ''}`}>
                <input type="checkbox"
                       checked={!!form.auto_relance}
                       onChange={e => up('auto_relance', e.target.checked)}
                       disabled={!form.contact_email}
                       className="w-4 h-4 mt-0.5 accent-[#008A8D] flex-shrink-0"
                       data-testid="auto-relance-checkbox" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">Relance automatique par email</p>
                  <p className="text-[11px] text-gray-600 mt-0.5">
                    {form.contact_email
                      ? <>Si actif, RealEase enverra automatiquement un email de relance a <b>{form.contact_email}</b> chaque semaine quand le delai est en alerte (orange/rouge).</>
                      : <>Renseignez d'abord un email d'interlocuteur ci-dessus pour activer cette option.</>}
                  </p>
                </div>
              </label>
            </div>
          )}

          {team.length > 0 && (
            <div>
              <label className="realease-label">Équipe assignée (taguer plusieurs collaborateurs)</label>
              <div className="flex flex-wrap gap-2" data-testid="assigned-users-picker">
                {team.map(u => {
                  const checked = (form.assigned_users || []).includes(u.user_id);
                  return (
                    <button key={u.user_id} type="button" onClick={() => toggleAssign(u.user_id)}
                            className={`px-3 py-1.5 text-xs rounded-full border-2 transition-colors ${checked ? 'bg-[#008A8D] text-white border-[#008A8D]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#008A8D]'}`}
                            data-testid={`tag-user-${u.user_id}`}>
                      {checked && '\u2713 '}{u.name || u.email}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div><label className="realease-label">Commentaire</label><textarea value={form.comment} onChange={e => up('comment', e.target.value)} className="realease-input h-20 resize-none" /></div>

          {/* Inclusion dans le previsionnel travaux */}
          <div className={`p-3 rounded-lg border-2 transition-colors ${form.include_in_works_schedule ? 'border-[#13736D] bg-[#13736D]/5' : 'border-gray-200 bg-white'}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox"
                     checked={!!form.include_in_works_schedule}
                     onChange={e => up('include_in_works_schedule', e.target.checked)}
                     className="w-4 h-4 mt-0.5 accent-[#13736D] flex-shrink-0"
                     data-testid="include-works-schedule-checkbox" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6h13M5 11V5a2 2 0 012-2h10a2 2 0 012 2v4M5 11h14M3 21h18" />
                  </svg>
                  Inclure dans le previsionnel travaux
                </p>
                <p className="text-[11px] text-gray-600 mt-0.5">
                  Ce delai apparaitra dans la timeline travaux du projet, visible cote equipe et cote client (portail).
                </p>
              </div>
            </label>
            {form.include_in_works_schedule && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="works-schedule-fields">
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 uppercase">Phase</label>
                  <input type="text" value={form.works_phase || ''} onChange={e => up('works_phase', e.target.value)}
                         placeholder="Maconnerie, Plomberie..."
                         className="realease-input text-sm"
                         data-testid="works-phase-input" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 uppercase">Entreprise / artisan</label>
                  <input type="text" value={form.works_company || ''} onChange={e => up('works_company', e.target.value)}
                         placeholder="Nom de l'entreprise"
                         className="realease-input text-sm"
                         data-testid="works-company-input" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 uppercase">Budget HT (EUR)</label>
                  <input type="number" min="0" step="100" value={form.works_budget_ht ?? ''}
                         onChange={e => up('works_budget_ht', e.target.value)}
                         placeholder="ex: 12500"
                         className="realease-input text-sm"
                         data-testid="works-budget-input" />
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <AutoSaveIndicator status={status} savedAt={savedAt} error={error} />
            <button type="button" onClick={handleClose}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      recordId ? 'bg-[#008A8D] text-white hover:bg-[#006D72]' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid="delay-close-btn">
              {recordId ? 'Fermer' : 'Annuler'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
